/**
 * Zentrale API-Hilfsfunktionen mit JWT-Token-Verwaltung.
 * Der Token kommt vom Express-Backend (/api/auth/login).
 */

const TOKEN_KEY = 'aiqcon_token';

export const getToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

export const setToken = (token) => {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {}
};

export const clearToken = () => setToken(null);

/**
 * fetch mit automatischem Bearer-Token.
 * @param {string} path - z.B. '/api/shared-kalkulationen'
 * @param {object} options - fetch-Optionen
 */
export const apiFetch = async (path, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  return fetch(path, { ...options, headers });
};

/**
 * Login: holt JWT-Token vom Backend.
 * @returns {{ ok: boolean, user?: object, error?: string }}
 */
export const apiLogin = async (email, password) => {
  try {
    const resp = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await resp.json();
    if (!resp.ok) return { ok: false, error: data.error || 'Anmeldung fehlgeschlagen' };
    setToken(data.token);
    return { ok: true, user: data.user };
  } catch (err) {
    return { ok: false, error: 'Server nicht erreichbar: ' + err.message };
  }
};

/**
 * SSO: Magic-Link-Token (aus GHL) gegen ein JWT eintauschen und Session setzen.
 * @param {string} magicToken - der per-User Magic-Token aus dem GHL-Link
 * @returns {{ ok: boolean, user?: object, error?: string }}
 */
export const apiSsoExchange = async (magicToken) => {
  try {
    const resp = await fetch('/api/sso/ghl-exchange', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: magicToken }),
    });
    const data = await resp.json();
    if (!resp.ok) return { ok: false, error: data.error || 'Magic-Link ungueltig' };
    setToken(data.token);
    return { ok: true, user: data.user };
  } catch (err) {
    return { ok: false, error: 'Server nicht erreichbar: ' + err.message };
  }
};

/**
 * User-basiertes SSO (geteilter GHL-Link mit {{user.email}}).
 * @param {string} email - E-Mail des eingeloggten GHL-Users
 * @param {string} secret - gemeinsames Geheimnis aus dem Link
 * @param {string} [name] - optionaler Anzeigename
 */
export const apiSsoExchangeUser = async (email, secret, name) => {
  try {
    const resp = await fetch('/api/sso/ghl-user', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, secret, name }),
    });
    const data = await resp.json();
    if (!resp.ok) return { ok: false, error: data.error || 'Anmeldung fehlgeschlagen' };
    setToken(data.token);
    return { ok: true, user: data.user };
  } catch (err) {
    return { ok: false, error: 'Server nicht erreichbar: ' + err.message };
  }
};

/**
 * Aktuellen User aus dem Token laden (Session wiederherstellen).
 */
export const apiMe = async () => {
  const token = getToken();
  if (!token) return null;
  try {
    const resp = await apiFetch('/api/auth/me');
    if (!resp.ok) { clearToken(); return null; }
    return await resp.json();
  } catch {
    return null;
  }
};
